import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, IconButton, InputAdornment } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import UserSelectDialog from 'components/UserSelectDialog';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { enum2NumberArray, getFullName } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash';
import { OrderActionEnum } from 'models/enums/orderActionEnum';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  CheckboxElement,
  FormContainer,
  SelectElement,
  useForm,
} from 'react-hook-form-mui';
import { boolean, mixed, number, object } from 'yup';
import type { DatePickerProps } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';
import type { OrderActionInfo } from 'models/order/orderActionInfo';
import type { User } from 'models/user/user';
import type { UserShortInfo } from 'models/user/userShortInfo';
import type { FC } from 'react';
import type { Schema } from 'yup';

interface FormData extends Pick<OrderActionInfo, 'action' | 'user' | 'current'> {
  dateAction: Dayjs | null;
}

const schema: Schema<FormData> = object().shape({
  dateAction: mixed<Dayjs>().defined().nullable(),
  action: number().oneOf(enum2NumberArray(OrderActionEnum)).required(),
  current: boolean().default(false),
  user: mixed<UserShortInfo>((v): v is UserShortInfo => !isEmpty(v.fullName)).optional(),
});

const minActionDate = dayjs().add(-1, 'week');
const minActionMonth = minActionDate.add(-1, 'month');
const minActionYear = minActionDate.add(-1, 'year');
const datesDisable: Pick<
  DatePickerProps<Dayjs>,
  'shouldDisableDate' | 'shouldDisableMonth' | 'shouldDisableYear'
> = {
  shouldDisableDate: (date) => date < minActionDate,
  shouldDisableMonth: (date) => date < minActionMonth,
  shouldDisableYear: (date) => date < minActionYear,
};

const ActionEditDialog: FC<ActionEditDialogProps> = ({
  open,
  action,
  onSave,
  onCancel,
  whiteList,
}) => {
  const [openUserSelectDialog, setOpenUserSelectDialog] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [availableActions, setAvailableActions] = useState(
    enum2NumberArray(OrderActionEnum)
  );
  const formContext = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    formContext.reset({
      action: action.action,
      dateAction: !isNil(action.dateAction) ? dayjs(action.dateAction) : null,
      user: action.user,
      current: action.current,
    });
  }, [formContext, action]);

  useEffect(() => {
    setAvailableActions(whiteList);
  }, [whiteList]);

  const handleOnSave: VoidFunction = () => {
    const formDateAction = formContext.getValues().dateAction;
    const dateAction = !isNil(formDateAction)
      ? new Date(formDateAction.toISOString())
      : undefined;

    const editedAction = {
      ...action,
      ...formContext.getValues(),
      dateAction,
    };

    if (!isNil(onSave)) {
      setSaving(true);
      onSave(editedAction)
        .finally(() => {
          setSaving(false);
        })
        .catch(console.log);
    }
  };

  const userSelectCancelHandler: VoidFunction = () => {
    setOpenUserSelectDialog(false);
  };

  const userSelectSaveHandler = (users: User[]): void => {
    setOpenUserSelectDialog(false);

    if (users.length > 0 && !isNil(users[0])) {
      onUserChange(users[0]);
    }
  };

  const onUserChange = (user: User): void => {
    formContext.setValue('user', {
      id: user.id,
      fullName: getFullName(user),
    });
  };

  const getTitle = (): string =>
    action.id < 0 ? 'Новый этап' : getOrderActionName(action.action);

  const getSelectedUserName = (): string => {
    const user = formContext.getValues().user;
    return !isNil(user) ? user.fullName : 'Не задан';
  };

  return (
    <SimpleDialog
      title={getTitle()}
      open={open}
      onClose={onCancel}
      actions={
        <>
          <LoadingButton
            loading={saving}
            onClick={() => {
              void formContext.handleSubmit(handleOnSave)();
            }}>
            <span>Сохранить</span>
          </LoadingButton>
          <Button onClick={onCancel}>Закрыть</Button>
        </>
      }
      content={
        <>
          <FormContainer formContext={formContext} onSuccess={handleOnSave}>
            <SelectElement
              label='Этап'
              name='action'
              margin='normal'
              fullWidth
              required
              options={availableActions.map((v) => ({
                id: v,
                label: getOrderActionName(v),
              }))}
            />

            <Controller
              name={'dateAction'}
              control={formContext.control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  {...datesDisable}
                  label={'Дата'}
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () => {
                        field.onChange(null);
                      },
                    },
                    textField: {
                      margin: 'normal',
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            <TextField
              fullWidth
              label='Ответственный'
              margin='normal'
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      edge='end'
                      onClick={() => {
                        setOpenUserSelectDialog(true);
                      }}>
                      <EditIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={getSelectedUserName()}
            />

            <CheckboxElement label='Текущий' name='current' />
          </FormContainer>

          {!isNil(formContext.getValues().action) && (
            <UserSelectDialog
              open={openUserSelectDialog}
              actionAccesses={[formContext.getValues().action]}
              description={`Список сотрудников, работающих на этапе: ${
                !isNil(formContext.getValues().action)
                  ? getOrderActionName(formContext.getValues().action ?? 0)
                  : ''
              }`}
              onCancel={userSelectCancelHandler}
              onSave={userSelectSaveHandler}
            />
          )}
        </>
      }
    />
  );
};

export interface ActionEditDialogProps {
  action: OrderActionInfo;
  open: boolean;
  whiteList: OrderActionEnum[];
  onSave?: (payment: OrderActionInfo) => Promise<boolean>;
  onCancel?: VoidFunction;
}

export default ActionEditDialog;
